/** Survey admin helpers — filters, stats, CSV, pain chart data */

import { getQuestionsForType } from '../components/surveyTranslations';

export const OWNER_PAIN_IDS = [];
export const CLIENT_PAIN_IDS = [];

export const PAIN_LABELS_MR = {
  r_location: 'Location',
  r_side_booking: 'Side booking',
  r_idling: 'Idling',
  r_ledger: 'हिशोब',
  r_calls: 'कॉल',
  r_punctuality: 'वेळ',
  r_rate_change: 'दर',
  r_hours: 'तास',
  r_bill: 'Bill',
  r_reliability: 'विश्वास',
};

export const PAIN_I18N_KEYS = {
  r_location: 'surveyPainLocation',
  r_side_booking: 'surveyPainSideBooking',
  r_idling: 'surveyPainIdling',
  r_ledger: 'surveyPainLedger',
  r_calls: 'surveyPainCalls',
  r_punctuality: 'surveyPainPunctuality',
  r_rate_change: 'surveyPainRate',
  r_hours: 'surveyPainHours',
  r_bill: 'surveyPainBill',
  r_reliability: 'surveyPainReliability',
};

export const LANG_LABELS = {
  mr: 'मराठी',
  hi: 'हिंदी',
  en: 'English',
};

export function relativeTime(iso, t) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return t('surveyTimeJustNow');
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('surveyTimeJustNow');
  if (mins < 60) return t('surveyTimeMinutesAgo').replace('{n}', String(mins));
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('surveyTimeHoursAgo').replace('{n}', String(hrs));
  const days = Math.floor(hrs / 24);
  return t('surveyTimeDaysAgo').replace('{n}', String(days));
}

/** @deprecated use relativeTime(iso, t) */
export function relativeTimeMr(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'आत्ताच';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'आत्ताच';
  if (mins < 60) return `${mins} मिनिटांपूर्वी`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} तासांपूर्वी`;
  const days = Math.floor(hrs / 24);
  return `${days} दिवसांपूर्वी`;
}

export function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

export function computeSurveyStats(rows) {
  const list = rows || [];
  const hotLeads = list.filter((r) => r.hot_lead === true).length;
  const today = list.filter((r) => isToday(r.submitted_at)).length;
  const sourceCounts = {};
  list.forEach((r) => {
    const src = (r.source || '').trim() || '(none)';
    sourceCounts[src] = (sourceCounts[src] || 0) + 1;
  });
  let topSource = '—';
  let topCount = 0;
  Object.entries(sourceCounts).forEach(([src, count]) => {
    if (count > topCount) {
      topCount = count;
      topSource = src;
    }
  });
  return {
    total: list.length,
    hotLeads,
    today,
    topSource: topCount > 0 ? `${topSource} (${topCount})` : '—',
  };
}

export function getUniqueSurveyors(rows) {
  const set = new Set();
  (rows || []).forEach((r) => {
    const s = (r.surveyor || '').trim();
    if (s) set.add(s);
  });
  return [...set].sort();
}

export function filterSurveyRows(rows, filters) {
  return (rows || []).filter((row) => {
    if (filters.surveyType !== 'all' && row.survey_type !== filters.surveyType) return false;
    if (filters.hotLeadOnly && !row.hot_lead) return false;
    if (filters.language !== 'all' && row.language !== filters.language) return false;
    if (filters.surveyor !== 'all') {
      const s = (row.surveyor || '').trim();
      if (s !== filters.surveyor) return false;
    }
    if (filters.paymentStatus !== 'all') {
      const status = row.is_legacy ? 'legacy' : (row.payment_status || 'pending');
      if (status !== filters.paymentStatus) return false;
    }
    return true;
  });
}

export function averagePainRatings(rows, painIds, getLabel = (id) => PAIN_LABELS_MR[id] || id) {
  const sums = {};
  const counts = {};
  painIds.forEach((id) => {
    sums[id] = 0;
    counts[id] = 0;
  });
  (rows || []).forEach((row) => {
    const answers = row.answers || {};
    painIds.forEach((id) => {
      const raw = answers[id];
      if (raw == null || raw === '') return;
      const num = Number.parseInt(String(raw), 10);
      if (Number.isNaN(num) || num < 1 || num > 5) return;
      sums[id] += num;
      counts[id] += 1;
    });
  });
  return painIds.map((id) => ({
    id,
    label: getLabel(id) || id,
    avg: counts[id] > 0 ? Math.round((sums[id] / counts[id]) * 10) / 10 : 0,
    count: counts[id],
  }));
}

function optionTextById(question, optionId) {
  if (!question?.options) return optionId;
  const opt = question.options.find((o) => o.id === optionId);
  return opt?.text || optionId;
}

/** Resolve stored option id(s) to human-readable text for admin display. */
export function resolveSurveyAnswerDisplay(surveyType, lang, questionId, value) {
  if (value == null || value === '') return '—';
  const questions = getQuestionsForType(lang || 'mr', surveyType || 'owner');
  const question = questions[questionId];
  if (!question) {
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((id) => optionTextById(question, id)).join(', ');
  }
  return optionTextById(question, value);
}

export function formatAnswersLines(answers, { surveyType, lang } = {}) {
  if (!answers || typeof answers !== 'object') return [];
  const type = surveyType || 'owner';
  const langKey = lang || 'mr';
  const questions = getQuestionsForType(langKey, type);
  return Object.entries(answers).map(([key, val]) => {
    const qLabel = questions[key]?.label || key;
    const display = resolveSurveyAnswerDisplay(type, langKey, key, val);
    return `${qLabel} → ${display}`;
  });
}

export function surveyCsvFilename() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `survey-responses-${y}-${m}-${day}.csv`;
}

export function surveyRowsToCsvData(rows) {
  return (rows || []).map((row) => [
    row.submitted_at || '',
    row.survey_type || '',
    row.respondent_name || '',
    row.whatsapp_number || '',
    row.village || '',
    row.hot_lead ? 'true' : 'false',
    row.language || '',
    row.source || '',
    row.surveyor || '',
    row.is_legacy ? 'legacy' : (row.payment_status || ''),
    row.payout_locked ? 'true' : 'false',
    JSON.stringify(row.answers || {}),
  ]);
}

export const SURVEY_CSV_HEADERS = [
  'submitted_at',
  'survey_type',
  'respondent_name',
  'whatsapp_number',
  'village',
  'hot_lead',
  'language',
  'source',
  'surveyor',
  'payment_status',
  'payout_locked',
  'answers',
];
